/**
 * Cat Navigation Manager - CSS-First Approach
 */
const CatNavigationManager = (() => {
  
  const config = {
    selectors: {
      header: '.theme__header',
      collectionNavWrapper: '.js-collection-nav-wrapper',
      searchField: '#fs-search-field',
      searchFieldValue: '#search-field-value',
      announcementWrapper: '.announcement__wrapper',
      collectionTextContent: '.collection__text-content'
    },
    classes: {
      headerHidden: 'header-hidden',
      atTop: 'at-top',
      headerHiddenFallback: 'header-hidden-fallback',
      stickyActive: 'sticky-active'
    },
    cssProperties: {
      headerHeight: '--header-height',
      collectionNavWrapperHeight: '--collection-nav-wrapper-height',
      searchHeight: '--search-height',
      announcementHeight: '--announcement-height',
      stickyThreshold: '--sticky-threshold'
    },
    thresholds: {
      scroll: 50,
      top: 10
    }
  };
  
  let lastScrollY = 0;
  let isScrollingDown = false;
  let stickyThreshold = 0;
  let originalNavPosition = 0;

  // DOM elements
  const $header = document.querySelector(config.selectors.header);
  const $collectionNavWrapper = document.querySelector(config.selectors.collectionNavWrapper);
  const $searchField = document.querySelector(config.selectors.searchField);
  const $searchFieldValue = document.querySelector(config.selectors.searchFieldValue);
  const $announcementWrapper = document.querySelector(config.selectors.announcementWrapper);
  const $collectionTextContent = document.querySelector(config.selectors.collectionTextContent);

  // globals
  const isCollectionPage = document.body.classList.contains('template-collection');
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  let ticking = false;

  /**
   * Calculate the sticky threshold and store original nav position
   */
  function calculateStickyThreshold() {
    if (isMobile) return;
    
    if ($collectionTextContent) {
      const rect = $collectionTextContent.getBoundingClientRect();
      stickyThreshold = rect.top - $header.offsetHeight;
      document.documentElement.style.setProperty(config.cssProperties.stickyThreshold, `${stickyThreshold}px`);
    } else {
      stickyThreshold = 10;
      document.documentElement.style.setProperty(config.cssProperties.stickyThreshold, `${stickyThreshold}px`);
    }
  }

  /**
   * Measure original positions in static state
   */
  function measureOriginalPositions() {
    if (isMobile) {
      const colTextContentRect = $collectionTextContent ? $collectionTextContent.getBoundingClientRect() : null;
      const headerRect = $header.getBoundingClientRect();
      
      if (isCollectionPage && $collectionNavWrapper) {
        originalNavPosition = $collectionTextContent ? colTextContentRect.bottom + window.scrollY : headerRect.bottom + window.scrollY;
      } else if (!isCollectionPage && $searchField) {
        const searchRect = $searchField.getBoundingClientRect();
        originalNavPosition = searchRect.top + window.scrollY;
      } else if (!isCollectionPage && $collectionNavWrapper) {
        const navRect = $collectionNavWrapper.getBoundingClientRect();
        originalNavPosition = navRect.bottom + window.scrollY;
      }
      return;
    }

    const originalBodyClasses = document.body.className;
    document.body.classList.remove(config.classes.stickyActive);
    document.body.classList.remove(config.classes.headerHiddenFallback);
    document.body.classList.add(config.classes.atTop);

    document.body.offsetHeight;

    const colTextContentRect = $collectionTextContent ? $collectionTextContent.getBoundingClientRect() : null;
    const headerRect = $header.getBoundingClientRect();
    
    if (isCollectionPage && $collectionNavWrapper) {
      originalNavPosition = $collectionTextContent ? colTextContentRect.bottom + window.scrollY : headerRect.bottom + window.scrollY;
    } else if (!isCollectionPage && $searchField) {
      const searchRect = $searchField.getBoundingClientRect();
      originalNavPosition = searchRect.top + window.scrollY;
    } else if (!isCollectionPage && $collectionNavWrapper) {
      const navRect = $collectionNavWrapper.getBoundingClientRect();
      originalNavPosition = navRect.bottom + window.scrollY;
    }

    document.body.className = originalBodyClasses;
  }

  /**
   * Update CSS custom properties with actual element heights
   */
  function updateHeights() {
    if ($header) {
      const headerHeight = $header.offsetHeight;
      document.documentElement.style.setProperty(config.cssProperties.headerHeight, `${headerHeight}px`);
    } else {
      document.documentElement.style.setProperty(config.cssProperties.headerHeight, '0px');
    }

    if ($collectionNavWrapper) {
      const collectionNavWrapperHeight = $collectionNavWrapper.offsetHeight;
      document.documentElement.style.setProperty(config.cssProperties.collectionNavWrapperHeight, `${collectionNavWrapperHeight}px`);
    } else {
      document.documentElement.style.setProperty(config.cssProperties.collectionNavWrapperHeight, '0px');
    }
    
    if ($searchField) {
      const searchFieldStyle = getComputedStyle($searchField);
      const searchFieldValueStyle = getComputedStyle($searchFieldValue);
      const marginBottom = parseInt(searchFieldValueStyle.getPropertyValue('margin-bottom'));
      const paddingTop = parseInt(searchFieldStyle.getPropertyValue('padding-top'));
      const searchHeight = $searchFieldValue.getBoundingClientRect().height;
      document.documentElement.style.setProperty(config.cssProperties.searchHeight, `${searchHeight + marginBottom + paddingTop}px`);
    } else {
      document.documentElement.style.setProperty(config.cssProperties.searchHeight, '0px');
    }
    
    if ($announcementWrapper) {
      const announcementHeight = $announcementWrapper.offsetHeight;
      document.documentElement.style.setProperty(config.cssProperties.announcementHeight, `${announcementHeight}px`);
    } else {
      document.documentElement.style.setProperty(config.cssProperties.announcementHeight, '0px');
    }

    calculateStickyThreshold();
    measureOriginalPositions();
  }

  /**
   * Handle scroll state
   */
  function handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - lastScrollY;
    
    if (Math.abs(scrollDifference) > 5) {
      isScrollingDown = scrollDifference > 0;
    }

    if (!$header) return;

    if (currentScrollY <= config.thresholds.top) {
      $header.classList.remove(config.classes.headerHidden);
      document.body.classList.add(config.classes.atTop);
      document.body.classList.remove(config.classes.stickyActive);
      document.body.classList.remove(config.classes.headerHiddenFallback);
      lastScrollY = currentScrollY;
      return;
    }

    document.body.classList.remove(config.classes.atTop);
    
    const hasPassedOriginalPosition = currentScrollY < (originalNavPosition - $header.offsetHeight - 20);
    
    if (hasPassedOriginalPosition && !isScrollingDown) {
      document.body.classList.remove(config.classes.stickyActive);
      $header.classList.remove(config.classes.headerHidden);
      document.body.classList.remove(config.classes.headerHiddenFallback);
      lastScrollY = currentScrollY;
      return;
    }
    
    const hasPassedCollectionContent = currentScrollY > stickyThreshold;
    
    let shouldActivateSticky = false;
    if ($header && hasPassedCollectionContent) {
      if (Math.abs(currentScrollY - stickyThreshold) < 10) {
        const headerRect = $header.getBoundingClientRect();
        
        if (isCollectionPage && $collectionNavWrapper) {
          const navRect = $collectionNavWrapper.getBoundingClientRect();
          shouldActivateSticky = navRect.top <= headerRect.bottom;
        }
        else if (!isCollectionPage && $searchField) {
          const searchRect = $searchField.getBoundingClientRect();
          shouldActivateSticky = searchRect.top <= headerRect.bottom;
        }
      } else {
        if (isCollectionPage && $collectionNavWrapper) {
          shouldActivateSticky = true;
        } else if (!isCollectionPage && $searchField) {
          shouldActivateSticky = true;
        }
      }
    }

    if (shouldActivateSticky) {
      document.body.classList.add(config.classes.stickyActive);
      
      if (isScrollingDown && currentScrollY > (stickyThreshold + $header.offsetHeight + config.thresholds.scroll)) {
        $header.classList.add(config.classes.headerHidden);
        document.body.classList.add(config.classes.headerHiddenFallback);
      } else if (!isScrollingDown) {
        $header.classList.remove(config.classes.headerHidden);
        document.body.classList.remove(config.classes.headerHiddenFallback);
      }
    } else {
      document.body.classList.remove(config.classes.stickyActive);
      $header.classList.remove(config.classes.headerHidden);
      document.body.classList.remove(config.classes.headerHiddenFallback);
    }

    lastScrollY = currentScrollY;
  }

  /**
   * Throttled scroll handler for performance
   */
  function throttledScrollHandler() {
    if (!ticking) {
      requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * Initialize the navigation manager
   */
  function init() {
    updateHeights();
    window.addEventListener('resize', updateHeights);
    
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });

    handleScroll();
    document.fonts?.ready.then(updateHeights);
    setTimeout(updateHeights, 100);
  }

  /**
   * Cleanup
   */
  function destroy() {
    window.removeEventListener('scroll', throttledScrollHandler);
    window.removeEventListener('resize', updateHeights);
  }

  return {
    init,
    destroy,
    config
  };
})();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', CatNavigationManager.init);
} else {
  CatNavigationManager.init();
} 
